import { z } from 'zod'
import { ADMIN_ROLES } from '../shared/permissions'

/* Spec COG-009 — Admin › Equipe e permissões (/api/v1/admin/equipe).
   GET  /admin/equipe                      -> EquipeData
   POST /admin/equipe/invite               InviteInput       (equipe.gerenciar + admin ativo; terceiro: empresa + validoAte futuro)
   POST /admin/equipe/invite/cancel        { personId }      (same rule; only a pending invite — offboarding has no undo)
   POST /admin/equipe/offboard             { personId }      -> OffboardResult
   POST /admin/equipe/admin/activate       { horas, motivo } (equipe.gerenciar)
   POST /admin/equipe/admin/end            {}
   POST /admin/equipe/review/start         {}                (equipe.gerenciar + admin ativo)
   Example data in memory: changes here do not change PANEL_ADMIN_ROLES yet. */

export const PERSON_KINDS = ['colaborador', 'terceiro'] as const
export const PERSON_STATUSES = ['ativo', 'convidado', 'desligado', 'expirado'] as const
export const LOG_KINDS = ['acesso', 'admin', 'export', 'estorno', 'descarte', 'salvaguarda', 'retencao', 'vercomo'] as const
export type LogKind = (typeof LOG_KINDS)[number]

export const personSchema = z.object({
    id: z.string(),
    name: z.string(),
    /** Masked: "ra•••@childfundbrasil.org.br". */
    emailMasked: z.string(),
    department: z.string(),
    jobTitle: z.string(),
    role: z.enum(ADMIN_ROLES),
    kind: z.enum(PERSON_KINDS),
    status: z.enum(PERSON_STATUSES),
    /** Terceiro only. */
    company: z.string().nullable(),
    validUntil: z.string().nullable(),
    /** Left the company in HR but still has access. */
    leftInHr: z.boolean(),
    offboardedAt: z.string().nullable(),
    lastSeen: z.string().nullable(),
})
export type Person = z.infer<typeof personSchema>

export const logEntrySchema = z.object({
    at: z.string(),
    kind: z.enum(LOG_KINDS),
    who: z.string(),
    text: z.string(),
})
export type LogEntry = z.infer<typeof logEntrySchema>

export const equipeDataSchema = z.object({
    dataMode: z.enum(['example', 'live']),
    now: z.string(),
    people: z.array(personSchema),
    departments: z.array(z.string()),
    /** People who left in HR and still have access (ids). */
    leftWithAccess: z.array(z.string()),
    adminSession: z.object({
        active: z.boolean(),
        until: z.string().nullable(),
        reason: z.string().nullable(),
    }),
    review: z.object({
        lastAt: z.string(),
        lastBy: z.string(),
        dueAt: z.string(),
        inProgressSince: z.string().nullable(),
    }),
    log: z.array(logEntrySchema),
})
export type EquipeData = z.infer<typeof equipeDataSchema>

export const inviteInputSchema = z.object({
    name: z.string().trim().min(3).max(80),
    email: z.string().trim().toLowerCase().regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/),
    department: z.string().trim().min(2).max(80),
    role: z.enum(ADMIN_ROLES),
    kind: z.enum(PERSON_KINDS),
    company: z.string().trim().max(80).optional(),
    /** "2026-12-31" — required for terceiro, must be in the future. */
    validUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
})
export type InviteInput = z.infer<typeof inviteInputSchema>

export const inviteResultSchema = z.object({ ok: z.literal(true), message: z.string(), personId: z.string() })
export type InviteResult = z.infer<typeof inviteResultSchema>

export const cancelInviteInputSchema = z.object({ personId: z.string().regex(/^p[0-9]{1,4}$/) })
export type CancelInviteInput = z.infer<typeof cancelInviteInputSchema>

export const offboardInputSchema = z.object({ personId: z.string().regex(/^p[0-9]{1,4}$/) })
export type OffboardInput = z.infer<typeof offboardInputSchema>

export const offboardResultSchema = z.object({
    ok: z.literal(true),
    message: z.string(),
    steps: z.array(z.string()),
})
export type OffboardResult = z.infer<typeof offboardResultSchema>

export const ADMIN_HOURS = [1, 4, 8] as const
export const activateInputSchema = z.object({
    horas: z.union([z.literal(1), z.literal(4), z.literal(8)]),
    motivo: z.string().trim().min(5).max(200),
})
export type ActivateInput = z.infer<typeof activateInputSchema>
